/**
 * P2PTransferPage — citizen-to-citizen wallet transfer by phone or email,
 * OR citizen-to-bank-account NIBSS transfer (Paystack-backed).
 *
 * Wired endpoints (live):
 *   - NIPOST mode  POST /v1/wallet/wallets-mine/transfers       (Spec 13)
 *   - BANK  mode   GET  /v1/bank/list                           (Paystack bank list)
 *                  GET  /v1/bank/resolve                        (account-name lookup)
 *                  POST /v1/transfers/bank                      (NIBSS instant transfer)
 *
 * RBAC: `Wallet/transfer-own`     (citizen P2P)
 *       `Wallet/transferBank-own` (citizen bank-rail)
 *       `Bank/resolve-own`        (citizen account-name lookup)
 *
 * Test-mode fallback inside @upmp/paystack-client makes bank-mode hermetic
 * for e2e: the bank list returns 5 hardcoded NG banks, the resolve returns
 * 'TEST ACCOUNT HOLDER', and the transfer auto-succeeds with reference
 * `BT-<uuid>`.
 */
import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, CheckCircle2, Loader2, Search } from 'lucide-react';

import {
  citizenTransfer,
  type CitizenTransferResponse,
} from '../../lib/api/wallet';
import {
  listBanks,
  resolveAccount,
  submitBankTransfer,
  type BankListResponse,
  type BankResolveResponse,
  type SubmitBankTransferResponse,
} from '../../lib/api/bank-transfer';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  PageHeader,
  customerWalletQueryKey,
  describeWalletError,
  formatNaira,
  useCustomerWallet,
} from './NipostPayUI';

type RecipientType = 'NIPOST' | 'BANK';

export function P2PTransferPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const wallet = useCustomerWallet(customerId, token);

  const [recipientType, setRecipientType] = useState<RecipientType>('NIPOST');
  const [recipientPhone, setRecipientPhone] = useState<string>('');
  const [bankCode, setBankCode] = useState<string>('');
  const [bankAccount, setBankAccount] = useState<string>('');
  const [resolvedName, setResolvedName] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [memo, setMemo] = useState<string>('');

  // Bank list — public endpoint, cached server-side 1h.
  const banksQuery = useQuery<BankListResponse, Error>({
    queryKey: ['bank-list', 'nigeria'],
    queryFn: () => listBanks('nigeria', { bearerToken: token ?? undefined }),
    enabled: recipientType === 'BANK',
    staleTime: 60 * 60 * 1000,
  });

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- React Query's useMutation third generic is the variables type; `void` is the canonical "no variables" marker
  const resolveMut = useMutation<BankResolveResponse, Error, void>({
    mutationFn: () =>
      resolveAccount(bankAccount.trim(), bankCode, { bearerToken: token ?? undefined }),
    onSuccess: (data) => {
      setResolvedName(data.accountName);
    },
    onError: () => {
      setResolvedName('');
    },
  });

  const transfer = useMutation<CitizenTransferResponse, Error, undefined>({
    mutationFn: () => {
      if (!wallet.data) throw new Error('Wallet not loaded.');
      const phone = recipientPhone.trim();
      const e164 = phone.startsWith('0') ? `+234${phone.slice(1)}` : phone;
      return citizenTransfer(
        {
          recipientPhone: e164.length > 0 ? e164 : undefined,
          amount: amount.length > 0 ? amount : '0',
          currency: wallet.data.currency,
          memo: memo.length > 0 ? memo : undefined,
          idempotencyKey: `p2p-${Date.now()}`,
        },
        { bearerToken: token ?? undefined },
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) }),
  });

  const bankTransfer = useMutation<SubmitBankTransferResponse, Error, undefined>({
    mutationFn: () => {
      // eslint-disable-next-line upmp/no-number-money -- amount → kobo conversion at the wire boundary
      const minor = Math.round(Number(amount) * 100);
      return submitBankTransfer(
        {
          recipientAccountNumber: bankAccount.trim(),
          recipientBankCode: bankCode,
          recipientName: resolvedName,
          amountMinor: minor,
          currency: 'NGN',
          narration: memo.length > 0 ? memo : undefined,
        },
        { bearerToken: token ?? undefined },
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) }),
  });

  // eslint-disable-next-line upmp/no-number-money -- display-only fee preview
  const requested = amount ? Number(amount) : 0;
   
  const fee = requested <= 5000 ? 52 : requested <= 50000 ? 500 : requested <= 200000 ? 750 : 1000;
  const total = requested + fee;

  const canSubmitBank =
    recipientType === 'BANK' &&
    requested > 0 &&
    bankCode.length > 0 &&
    /^\d{10}$/.test(bankAccount.trim()) &&
    resolvedName.length > 0;

  const canSubmitNipost =
    recipientType === 'NIPOST' && requested > 0 && recipientPhone.trim().length > 0;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (recipientType === 'BANK') {
      if (!canSubmitBank) return;
      bankTransfer.mutate(undefined);
    } else {
      if (!canSubmitNipost) return;
      transfer.mutate(undefined);
    }
  }

  return (
    <div>
      <PageHeader
        title="Send money"
        description="Transfer to another NIPOST Pay wallet or a Nigerian bank account."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form
          onSubmit={handleSubmit}
          className="lg:col-span-2 bg-white rounded-xl border border-border p-6 space-y-5"
          noValidate
        >
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-nipost-green" /> New transfer
          </h3>

          <div>
            <label className="block text-sm text-muted mb-1">Recipient type</label>
            <div className="flex gap-2">
              {(['NIPOST', 'BANK'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setRecipientType(t)}
                  className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                    recipientType === t
                      ? 'bg-nipost-green text-white border-nipost-green'
                      : 'border-border hover:bg-gray-50'
                  }`}
                >
                  {t === 'NIPOST' ? 'NIPOST Pay wallet' : 'Bank account'}
                </button>
              ))}
            </div>
          </div>

          {recipientType === 'NIPOST' ? (
            <div>
              <label htmlFor="p2p-recipient-phone" className="block text-sm text-muted mb-1">
                Recipient phone number
              </label>
              <input
                id="p2p-recipient-phone"
                type="tel"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                placeholder="+2348012345678 or 08012345678"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
              <p className="mt-1 text-xs text-muted">
                The recipient must have an active NIPOST Pay wallet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="bank-code-field" className="block text-sm text-muted mb-1">
                  Bank
                </label>
                <select
                  id="bank-code-field"
                  value={bankCode}
                  onChange={(e) => {
                    setBankCode(e.target.value);
                    setResolvedName('');
                  }}
                  disabled={banksQuery.isLoading}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green bg-white"
                >
                  <option value="">{banksQuery.isLoading ? 'Loading banks…' : 'Select a bank'}</option>
                  {banksQuery.data?.items.map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bank-account-field" className="block text-sm text-muted mb-1">
                  Account number
                </label>
                <input
                  id="bank-account-field"
                  value={bankAccount}
                  onChange={(e) => {
                    setBankAccount(e.target.value);
                    setResolvedName('');
                  }}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="0123456789"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
                />
              </div>
              <div className="sm:col-span-2 flex items-center gap-3">
                <button
                  type="button"
                  id="bank-resolve-btn"
                  onClick={() => resolveMut.mutate()}
                  disabled={
                    bankCode.length === 0 ||
                    !/^\d{10}$/.test(bankAccount.trim()) ||
                    resolveMut.isPending
                  }
                  className="px-3 py-2 text-sm rounded-lg border border-border bg-white hover:bg-gray-50 disabled:opacity-60 inline-flex items-center gap-2"
                >
                  {resolveMut.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  Resolve account name
                </button>
                {resolvedName ? (
                  <span
                    id="bank-resolved-name"
                    className="text-sm font-semibold text-success"
                    role="status"
                  >
                    {resolvedName}
                  </span>
                ) : null}
                {resolveMut.isError ? (
                  <span className="text-xs text-red-600" role="alert">
                    Unable to resolve account.
                  </span>
                ) : null}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="p2p-amount" className="block text-sm text-muted mb-1">
                Amount (₦)
              </label>
              <input
                id="p2p-amount"
                type="number"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
            <div>
              <label htmlFor="p2p-memo" className="block text-sm text-muted mb-1">
                Note (optional)
              </label>
              <input
                id="p2p-memo"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="e.g. School fees"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted">Fee</span>
              <span className="font-semibold">{formatNaira(fee)}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-muted">Total debit</span>
              <span className="font-semibold text-gray-900">{formatNaira(total)}</span>
            </div>
          </div>

          {transfer.isError ? (
            <p className="text-xs text-red-600 rounded-lg bg-red-50 border border-red-200 px-3 py-2" role="alert">
              {describeWalletError(transfer.error)}
            </p>
          ) : null}
          {bankTransfer.isError ? (
            <p
              id="bank-transfer-error"
              className="text-xs text-red-600 rounded-lg bg-red-50 border border-red-200 px-3 py-2"
              role="alert"
            >
              {describeWalletError(bankTransfer.error)}
            </p>
          ) : null}

          {transfer.isSuccess ? (
            <div
              className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
              role="status"
              aria-live="polite"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Transfer sent — new balance{' '}
              <strong>{formatNaira(transfer.data.senderBalance)}</strong>
              {' '}(ref {transfer.data.transferId.slice(0, 12)})
            </div>
          ) : null}

          {bankTransfer.isSuccess ? (
            <div
              id="bank-transfer-success"
              className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
              role="status"
              aria-live="polite"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Bank transfer {bankTransfer.data.status.toLowerCase()} — reference{' '}
              <strong>{bankTransfer.data.reference}</strong>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={
              (recipientType === 'BANK'
                ? !canSubmitBank || bankTransfer.isPending
                : !canSubmitNipost || transfer.isPending) ||
              !wallet.data
            }
            className="w-full py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {(recipientType === 'BANK' ? bankTransfer.isPending : transfer.isPending) ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowRightLeft className="w-4 h-4" />
            )}
            Send{amount ? ` — ${formatNaira(total)}` : ''}
          </button>
        </form>

        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-gray-900 mb-3">Fee schedule</h3>
            <div className="space-y-2 text-sm">
              {[
                ['Up to ₦5,000', '₦52'],
                ['₦5,001 – ₦50,000', '₦500'],
                ['₦50,001 – ₦200,000', '₦750'],
                ['Above ₦200,000', '₦1,000'],
              ].map(([range, f]) => (
                <div
                  key={range}
                  className="flex justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <span className="text-muted">{range}</span>
                  <span className="font-semibold text-gray-900">{f}</span>
                </div>
              ))}
            </div>
            {wallet.data ? (
              <p className="mt-4 text-xs text-muted">
                Your balance:{' '}
                <strong>{formatNaira(wallet.data.balanceCached ?? wallet.data.balance ?? '0')}</strong>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
